import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isTutorProfileComplete } from '../../utils/tutorProfileCompletion';
import { getTutorByUser } from '../../state/slices/tutor';

/**
 * GuestRoute component that redirects authenticated users to homepage
 * Used for pages that should only be accessible to non-logged-in users
 * (e.g., login, signup, forgot password pages)
 */
const GuestRoute = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userState = useSelector(state => state.user);
  const tutorState = useSelector(state => state.tutor);
  const isAuthenticated = userState?.user !== null && userState?.user !== undefined;
  const user = userState?.user;
  const currentUserId = user?.id || user?._id;
  const isTutor = user?.roles?.includes('TUTOR');

  const getTutorUserId = (tutor) => {
    if (!tutor?.userId) return null;
    if (typeof tutor.userId === 'string') return tutor.userId;
    return tutor.userId._id || tutor.userId.id || null;
  };

  const tutorDataBelongsToCurrentUser =
    !!tutorState?.tutor && getTutorUserId(tutorState.tutor) === currentUserId;
  const hasTutorData = tutorDataBelongsToCurrentUser;
  const tutorProfileComplete = hasTutorData ? isTutorProfileComplete(tutorState.tutor, t) : false;
  const [tutorCheckAttempted, setTutorCheckAttempted] = useState(false);

  useEffect(() => {
    setTutorCheckAttempted(false);
  }, [currentUserId]);

  useEffect(() => {
    if (!isAuthenticated || !isTutor || hasTutorData || tutorState.loading || tutorCheckAttempted) {
      return;
    }
    setTutorCheckAttempted(true);
    dispatch(getTutorByUser(currentUserId));
  }, [
    isAuthenticated,
    isTutor,
    hasTutorData,
    tutorState.loading,
    tutorCheckAttempted,
    dispatch,
    currentUserId,
  ]);

  // If user is logged in, redirect to homepage
  if (isAuthenticated) {
    // For tutors, wait until we attempt to resolve tutor profile data/completion
    // before deciding where to redirect.
    if (isTutor && !hasTutorData && !tutorCheckAttempted) {
      return null;
    }

    if (isTutor && tutorState.loading) {
      return null;
    }

    // If tutor record does not exist, treat as incomplete profile.
    if (isTutor && tutorCheckAttempted && !hasTutorData) {
      return <Navigate to="/tutor-profile" replace />;
    }

    if (isTutor && hasTutorData && !tutorProfileComplete) {
      return <Navigate to="/tutor-profile" replace />;
    }
    if (isTutor && hasTutorData && tutorProfileComplete) {
      return <Navigate to="/tutor-class-room" replace />;
    }
    return <Navigate to="/" replace />;
  }

  // If not logged in, allow access to the page
  return children;
};

export default GuestRoute;
